// Compute unique, category-appropriate images for a set of listings on the client
// Ensures: (1) category match, (2) no repeated demo asset on a page

export type MinimalListing = {
  id: string
  title: string
  sector: string
  current?: string | null
}

function removeDiacritics(str: string) {
  return str.normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
}

function withCacheBust(url: string) {
  if (!url) return url
  return url.includes('?') ? url : `${url}?v=3`
}

function isSeedPlaceholder(url?: string | null) {
  if (!url) return true
  return url.startsWith('/images/test-listings')
}

// Category pools from existing assets to maximize variety per category
const POOLS: Record<string, string[]> = {
  // Hospitality - general
  hospitality: [
    '/images/test-listings/cafe-interior.jpg',
    '/images/test-listings/bar.jpg',
    '/images/test-listings/restaurant-kitchen.jpg',
    '/images/test-listings/fine-dining.jpg',
    '/images/test-listings/luxury-hotel.jpg',
    '/images/test-listings/specialty-food.jpg',
  ],
  // Hospitality - Asian / takeaway focused
  hospitality_asian: [
    '/images/test-listings/specialty-food.jpg',
    '/images/test-listings/cafe-interior.jpg',
  ],
  // Coffee / roastery focus
  coffee: [
    '/images/test-listings/cafe-interior.jpg',
    '/images/test-listings/specialty-food.jpg',
  ],
  // Health & Beauty (no medical here)
  health_beauty: [
    '/images/test-listings/beauty-salon.jpg',
    '/images/test-listings/gym-fitness.jpg',
    '/images/test-listings/spa.jpg',
    '/images/test-listings/pet-grooming.jpg',
  ],
  // Medical & Dental
  medical: [
    '/images/test-listings/medical-clinic.jpg',
    '/images/test-listings/pathology-lab.jpg',
  ],
  // Retail (includes florist)
  retail: [
    '/images/test-listings/boutique-retail.jpg',
    '/images/test-listings/clothing-store.jpg',
    '/images/test-listings/gift-shop.jpg',
    '/images/test-listings/homewares-store.jpg',
    '/images/test-listings/online-store.jpg',
    '/images/test-listings/florist.jpg',
  ],
  // Services (generic office/services look)
  services: [
    '/images/test-listings/tech-office.jpg',
    '/images/test-listings/marketing-agency.jpg',
    '/images/test-listings/education-training.jpg',
    '/images/test-listings/it-services.jpg',
  ],
  // Specific services niches without perfect assets
  accounting: [
    '/images/test-listings/education-training.jpg',
  ],
  cleaning: [
    '/images/test-listings/painting-services.jpg',
  ],
  // Automotive
  automotive: [
    '/images/test-listings/car-wash.jpg',
    '/images/test-listings/car-detailing.jpg',
    '/images/test-listings/car-dealership.jpg',
    '/images/test-listings/mechanic-workshop.jpg',
  ],
  // Trades
  trades: [
    '/images/test-listings/landscaping.jpg',
    '/images/test-listings/plumbing.jpg',
    '/images/test-listings/painting-services.jpg',
  ],
  electrical: [
    '/images/test-listings/painting-services.jpg',
    '/images/test-listings/plumbing.jpg',
  ],
  hardware: [
    '/images/test-listings/homewares-store.jpg',
    '/images/test-listings/landscaping.jpg',
  ],
  childcare: [
    '/images/test-listings/education-training.jpg',
  ],
  manufacturing: [
    '/images/test-listings/manufacturing.jpg',
  ],
  online: [
    '/images/test-listings/online-store.jpg',
    '/images/test-listings/tech-office.jpg',
  ],
  general: [
    '/images/test-listings/cafe-interior.jpg',
    '/images/test-listings/tech-office.jpg',
    '/images/test-listings/boutique-retail.jpg',
    '/images/test-listings/marketing-agency.jpg',
  ],
}

function detectCategory(title: string, sector: string): keyof typeof POOLS {
  const t = removeDiacritics(`${title} ${sector}`.toLowerCase())

  // Highly specific phrases first
  if (/(asian|sushi|thai|chinese|noodle).*(takeaway|restaurant|food|kitchen)|\btakeaway\b/.test(t)) return 'hospitality_asian'
  if (/(coffee).*(roast|roaster|roastery)|\broastery\b/.test(t)) return 'coffee'
  if (/hardware|tools/.test(t)) return 'hardware'

  if (/(^|\b)electrical(\b|$)|(^|\b)electrician(\b|$)/.test(t)) return 'electrical'
  if (/plumb|drain/.test(t)) return 'trades'
  if (/paint|decor/.test(t)) return 'trades'
  if (/landscap|garden/.test(t)) return 'trades'

  if (/mechanic|auto|car wash|detailing|car-?wash|dealership|workshop/.test(t)) return 'automotive'

  if (/dental|dentist/.test(t)) return 'medical'
  if (/medical|clinic|pathology|lab|veterinary|physio|gp/.test(t)) return 'medical'
  if (/child\s?care|kindergarten|day ?care/.test(t)) return 'childcare'
  if (/beauty|salon|spa|fitness|gym|grooming/.test(t)) return 'health_beauty'

  if (/hotel|restaurant|cafe|coffee|bar|pub|bakery|dine/.test(t)) return 'hospitality'

  if (/florist|flower/.test(t)) return 'retail'
  if (/retail|boutique|clothing|fashion|gift|homeware|shop|store/.test(t)) return 'retail'

  if (/accounting|accountant|bookkeep|tax/.test(t)) return 'accounting'
  if (/cleaning/.test(t)) return 'cleaning'
  if (/real estate|security|printing|marketing|it|technology|saas|education|training|school|course/.test(t)) return 'services'

  if (/manufactur|engineering/.test(t)) return 'manufacturing'

  if (/online|e-?commerce|website/.test(t)) return 'online'

  return 'general'
}

function hashToIndex(seed: string, mod: number) {
  let h = 2166136261
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i)
    h += (h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24)
  }
  return Math.abs(h) % Math.max(1, mod)
}

// Prefer highly specific assets for certain phrases to improve accuracy
function preferredAsset(title: string, sector: string): string | null {
  const t = removeDiacritics(`${title} ${sector}`.toLowerCase())
  const prefs: Array<[RegExp, string]> = [
    [/\bflorist|flower\b/, '/images/test-listings/florist.jpg'],
    [/\bhardware|tools\b/, '/images/test-listings/homewares-store.jpg'],
    [/\bdental|dentist\b/, '/images/test-listings/medical-clinic.jpg'],
    [/\bmedical|clinic|pathology|lab|gp\b/, '/images/test-listings/medical-clinic.jpg'],
    [/\belectrical|electrician\b/, '/images/test-listings/painting-services.jpg'],
    [/\bcleaning\b/, '/images/test-listings/painting-services.jpg'],
    [/\btake\s?away\b/, '/images/test-listings/specialty-food.jpg'],
    [/(coffee|cof+ee).*(roast|roaster|roastery)|\broastery\b/, '/images/test-listings/cafe-interior.jpg'],
    [/\baccounting|accountant|bookkeep|tax\b/, '/images/test-listings/education-training.jpg'],
  ]
  for (const [re, asset] of prefs) {
    if (re.test(t)) return asset
  }
  return null
}

export function resolveListingImages(list: MinimalListing[]) {
  const used = new Set<string>()
  const result: Record<string, string> = {}

  for (const l of list) {
    const curr = l.current || null

    // Enforce category-accurate, unique visuals (ignore existing mismatched real images)
    // We no longer preserve non-placeholder current images here.


    const category = detectCategory(l.title, l.sector)
    const pool = POOLS[category] || POOLS.general

    // Prefer a highly specific asset when it matches and isn't used yet
    const pref = preferredAsset(l.title, l.sector)
    if (pref) {
      const prefBase = pref.split('?')[0]
      if (!used.has(prefBase)) {
        used.add(prefBase)
        result[l.id] = withCacheBust(pref)
        continue
      }
    }

    // Deterministic starting index from listing id to distribute variety
    let idx = hashToIndex(l.id, pool.length)

    // Ensure uniqueness on the current page by cycling until an unused asset is found
    for (let tries = 0; tries < pool.length; tries++) {
      const candidate = pool[(idx + tries) % pool.length]
      const candidateBase = candidate.split('?')[0]
      if (!used.has(candidateBase)) {
        used.add(candidateBase)
        result[l.id] = withCacheBust(candidate)
        break
      }
    }

    // Fallback in very rare case all were used
    if (!result[l.id]) {
      const fallback = curr || pool[0]
      result[l.id] = withCacheBust(fallback)
      used.add(result[l.id].split('?')[0])
    }
  }

  return result
}
